// Registers the service worker built from src/service-worker.js and exposes
// helpers the UI uses to drive the offline-download cache.

const isLocalhost = Boolean(
	window.location.hostname === "localhost" ||
		window.location.hostname === "[::1]" ||
		window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)){3}$/)
);

export function register(config) {
	if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) return;

	const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
	if (publicUrl.origin !== window.location.origin) return;

	window.addEventListener("load", () => {
		const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
		if (isLocalhost) {
			checkValidServiceWorker(swUrl, config);
		} else {
			registerValidSW(swUrl, config);
		}
	});
}

function registerValidSW(swUrl, config) {
	navigator.serviceWorker
		.register(swUrl)
		.then((registration) => {
			registration.onupdatefound = () => {
				const installing = registration.installing;
				if (!installing) return;
				installing.onstatechange = () => {
					if (installing.state !== "installed") return;
					if (navigator.serviceWorker.controller) {
						config && config.onUpdate && config.onUpdate(registration);
					} else {
						config && config.onSuccess && config.onSuccess(registration);
					}
				};
			};
		})
		.catch((error) => console.error("SW registration failed:", error));
}

function checkValidServiceWorker(swUrl, config) {
	fetch(swUrl, { headers: { "Service-Worker": "script" } })
		.then((response) => {
			const contentType = response.headers.get("content-type");
			if (response.status === 404 || (contentType && contentType.indexOf("javascript") === -1)) {
				navigator.serviceWorker.ready.then((registration) =>
					registration.unregister().then(() => window.location.reload())
				);
			} else {
				registerValidSW(swUrl, config);
			}
		})
		.catch(() => console.log("No internet connection; app is running in offline mode."));
}

export function unregister() {
	if (!("serviceWorker" in navigator)) return;
	navigator.serviceWorker.ready.then((r) => r.unregister()).catch((e) => console.error(e));
}

// --- Offline download helpers -------------------------------------------------

export const AUDIO_CACHE = "lita-audio-v1";

export function isOfflineSupported() {
	return "serviceWorker" in navigator && "caches" in window;
}

// Which of the given track URLs already have a complete body in the cache.
export async function getCachedUrls(urls) {
	if (!isOfflineSupported()) return new Set();
	try {
		const cache = await caches.open(AUDIO_CACHE);
		const hits = await Promise.all(urls.map(async (u) => ((await cache.match(u)) ? u : null)));
		return new Set(hits.filter(Boolean));
	} catch {
		return new Set();
	}
}

// Ask the SW to fetch and store each track. onProgress receives (done, total).
export function cacheTracks(urls, { onProgress, onDone, onError } = {}) {
	if (!isOfflineSupported()) {
		onError && onError(new Error("이 브라우저는 오프라인 저장을 지원하지 않습니다."));
		return;
	}
	navigator.serviceWorker.ready.then((registration) => {
		const worker = registration.active;
		if (!worker) {
			onError && onError(new Error("서비스 워커가 아직 활성화되지 않았습니다. 새로고침 후 다시 시도해 주세요."));
			return;
		}
		const onMessage = (event) => {
			const d = event.data || {};
			if (d.type === "CACHE_PROGRESS") onProgress && onProgress(d.done, d.total);
			if (d.type === "CACHE_DONE") {
				navigator.serviceWorker.removeEventListener("message", onMessage);
				onDone && onDone();
			}
			if (d.type === "CACHE_ERROR") {
				navigator.serviceWorker.removeEventListener("message", onMessage);
				onError && onError(new Error(d.message));
			}
		};
		navigator.serviceWorker.addEventListener("message", onMessage);
		worker.postMessage({ type: "CACHE_TRACKS", urls });
	});
}

export function clearTracks({ onDone, onError } = {}) {
	if (!isOfflineSupported()) return;
	navigator.serviceWorker.ready.then((registration) => {
		const worker = registration.active;
		if (!worker) {
			onError && onError(new Error("서비스 워커가 아직 활성화되지 않았습니다."));
			return;
		}
		const onMessage = (event) => {
			if ((event.data || {}).type !== "CACHE_CLEARED") return;
			navigator.serviceWorker.removeEventListener("message", onMessage);
			onDone && onDone();
		};
		navigator.serviceWorker.addEventListener("message", onMessage);
		worker.postMessage({ type: "CLEAR_TRACKS" });
	});
}

// Rough storage picture for the UI, where the browser exposes it.
export async function getStorageEstimate() {
	if (!navigator.storage || !navigator.storage.estimate) return null;
	try {
		return await navigator.storage.estimate();
	} catch {
		return null;
	}
}
